// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a Khipu's integrity (`docs/AYNI_XR_KHIPU_PLAN.md`, "Integrity"):
 * the hash chain over the log, the content hash over the whole record, and
 * the place a signature goes once AYNI has a signing key.
 *
 * - **Chain** (`integrity.chained`): each event's `h` is the SHA-256 of the
 *   previous event's `h` (empty for the first) followed by the event
 *   without its `h`, written as canonical JSON. Removing, reordering or
 *   editing an event changes every `h` after it; `integrity.head` is the
 *   last one.
 * - **Content** (`integrity.contentSha256`): the SHA-256 of the whole
 *   Khipu as canonical JSON, without `integrity.contentSha256` and
 *   `integrity.signature`. It covers the steps, the numbers, the
 *   attachments' own SHA-256 and the chain's head.
 * - **Signature** (phase 3, owner action): Ed25519 over the 32 bytes of the
 *   content hash, by a key AYNI's server holds (decided 2026-10-05: a key
 *   of its own, not the licence key; not generated yet). `signKhipu` takes
 *   any signer, so the server can sign once the key exists; nothing in the
 *   app requires one.
 *
 * Canonical JSON: object keys sorted by UTF-16 code unit, no whitespace,
 * numbers and strings as `JSON.stringify` writes them. Pure apart from
 * WebCrypto (`crypto.subtle`, in browsers, Workers and Node 20).
 */
import type { Khipu, KhipuLog } from './khipu'

/** JSON with every object's keys sorted, so the same record always gives the same bytes. */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    const s = JSON.stringify(value)
    // undefined, functions: absent, as JSON.stringify leaves them out of objects.
    return s === undefined ? 'null' : s
  }
  if (Array.isArray(value)) return `[${value.map((v) => canonicalJson(v === undefined ? null : v)).join(',')}]`
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined && typeof v !== 'function')
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(',')}}`
}

const hex = (bytes: ArrayBuffer | Uint8Array): string =>
  [...(bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes))].map((b) => b.toString(16).padStart(2, '0')).join('')

/** SHA-256 of text (as UTF-8) or bytes, lowercase hex. */
export async function sha256Hex(input: string | Uint8Array | ArrayBuffer | Blob): Promise<string> {
  let bytes: Uint8Array
  if (typeof input === 'string') bytes = new TextEncoder().encode(input)
  else if (input instanceof Uint8Array) bytes = input
  else if (input instanceof ArrayBuffer) bytes = new Uint8Array(input)
  else bytes = new Uint8Array(await input.arrayBuffer())
  return hex(await crypto.subtle.digest('SHA-256', bytes as BufferSource))
}

type KhipuEvent = Record<string, unknown> & { h?: string }

/** The events with their chain (`h`) set, and the head; the events are copied, not changed. */
export async function chainEvents(events: readonly KhipuEvent[]): Promise<{ events: KhipuEvent[]; head: string | null }> {
  let prev = ''
  const out: KhipuEvent[] = []
  for (const e of events) {
    const { h: _old, ...rest } = e
    const h = await sha256Hex(prev + canonicalJson(rest))
    out.push({ ...rest, h })
    prev = h
  }
  return { events: out, head: out.length ? prev : null }
}

interface Integrity {
  algorithm: 'sha256'
  chained?: boolean
  head?: string
  contentSha256?: string
  signature?: { keyId: string; value: string; signedAt?: string }
}

/** The record the content hash is taken over: everything but the hash and the signature. */
function hashable(k: Khipu): Khipu {
  const integrity = k.integrity as Integrity | undefined
  if (!integrity) return k
  const { contentSha256: _c, signature: _s, ...rest } = integrity
  return { ...k, integrity: rest }
}

/** The content hash of a Khipu as it stands. */
export function contentHash(k: Khipu): Promise<string> {
  return sha256Hex(canonicalJson(hashable(k)))
}

/**
 * Seal a Khipu: chain its log's events, then hash the whole. Any earlier
 * chain, hash or signature is replaced (a signature over other content
 * would be false).
 */
export async function sealKhipu(k: Khipu): Promise<Khipu> {
  const log = k.log
  const chained = log?.events ? await chainEvents(log.events as readonly KhipuEvent[]) : null
  const sealed: Khipu = {
    ...k,
    ...(log && chained ? { log: { ...log, events: chained.events } as KhipuLog } : {}),
    integrity: {
      algorithm: 'sha256',
      chained: !!chained,
      ...(chained?.head ? { head: chained.head } : {}),
    },
  }
  ;(sealed.integrity as Integrity).contentSha256 = await contentHash(sealed)
  return sealed
}

export interface IntegrityCheck {
  /** The chain is whole: null when the record has no chain. */
  chain: boolean | null
  /** Index of the first event whose `h` does not follow, or -1. */
  brokenAt: number
  /** The content hash matches: null when the record carries none. */
  content: boolean | null
}

/** Check a Khipu's chain and content hash (not the signature: that needs AYNI's public key). */
export async function verifyKhipu(k: Khipu): Promise<IntegrityCheck> {
  const integrity = k.integrity as Integrity | undefined
  let chain: boolean | null = null
  let brokenAt = -1
  const events = (k.log as { events?: KhipuEvent[] } | undefined)?.events
  if (integrity?.chained && events) {
    const redone = await chainEvents(events)
    brokenAt = events.findIndex((e, i) => e.h !== redone.events[i]?.h)
    chain = brokenAt === -1 && (redone.head ?? undefined) === integrity.head
  }
  const content = integrity?.contentSha256 ? (await contentHash(k)) === integrity.contentSha256 : null
  return { chain, brokenAt, content }
}

/**
 * Who signs (phase 3): AYNI's server, with an Ed25519 key of its own. Not
 * built: the key is an owner action (docs/AYNI_XR_KHIPU_PLAN.md, "Owner
 * decisions"). Any signer with this shape fits.
 */
export interface KhipuSigner {
  readonly keyId: string
  /** The signature over these bytes, base64. */
  sign(bytes: Uint8Array): Promise<string>
}

/** A sealed Khipu signed over its content hash's 32 bytes; unchanged without a signer. */
export async function signKhipu(k: Khipu, signer: KhipuSigner | null, now: Date = new Date()): Promise<Khipu> {
  const integrity = k.integrity as Integrity | undefined
  if (!signer || !integrity?.contentSha256) return k
  const bytes = new Uint8Array(integrity.contentSha256.match(/../g)!.map((b) => parseInt(b, 16)))
  const value = await signer.sign(bytes)
  return { ...k, integrity: { ...integrity, signature: { keyId: signer.keyId, value, signedAt: now.toISOString() } } }
}
